import { createMcsSubUser, createMcsSubUserToken } from '@/api/apiService';
import { MCSSubUser } from '@/types/socket';
import { getInitialSubUser } from '@/utils/constants/chat';
import { stringToBase64UrlSafe } from '@/utils/encryptor.js';

export default function useMCSSubUser(code: Ref<string>, nickname: string) {
  const subUser = ref<MCSSubUser>({ ...getInitialSubUser() });

  const isValidUser = computed<boolean>(() => {
    return Object.values(subUser.value).every((item) => !!item);
  });

  const initUser = (partial?: Partial<MCSSubUser>) => {
    Object.assign(subUser.value, { ...getInitialSubUser(), ...partial });
  };

  const requestCreateUser = async (): Promise<Pick<MCSSubUser, 'loginId' | 'userId' | 'nickname'>> => {
    const res = await createMcsSubUser(code.value, {
      nickname: stringToBase64UrlSafe(nickname),
    });

    if (!res.success || !res.data) {
      throw new Error('Failed to create sub user');
    }

    return {
      loginId: res.data.loginId,
      userId: res.data.userId,
      nickname: res.data.nickname,
    };
  };

  const requestAuthSubUser = async (id: string, nickname: string): Promise<Pick<MCSSubUser, 'authToken'>> => {
    const res = await createMcsSubUserToken({ nickname }, { Userid: id });

    if (!res.success || !res.data) {
      throw new Error('Failed to fetch sub user token');
    }

    return {
      authToken: res.data.token,
    };
  };

  const createUser = async () => {
    try {
      const { loginId, userId, nickname: resolvedNickname } = await requestCreateUser();
      const { authToken } = await requestAuthSubUser(loginId, resolvedNickname);

      initUser({ loginId, userId, authToken, nickname: resolvedNickname });
    } catch (e) {
      initUser();
      throw e;
    }
  };

  return {
    subUser: readonly(subUser),
    isValidUser,
    createUser,
  };
}
